<template>
  <div class="min-h-screen bg-bochas-bg">
    <!-- Loading -->
    <section v-if="loading" class="py-24 sm:py-32 text-center">
      <div
        class="inline-block w-10 h-10 sm:w-12 sm:h-12 border-4 border-bochas-rojo border-t-transparent rounded-full animate-spin"
      ></div>
    </section>

    <!-- Error / 404 -->
    <section
      v-else-if="!joven"
      class="max-w-7xl mx-auto px-4 py-12 lg:py-24 text-center"
    >
      <h1 class="text-2xl sm:text-3xl font-bold text-white mb-3">
        No encontrado
      </h1>
      <p class="text-gray-400 mb-6">No encontramos este joven.</p>
      <router-link
        to="/jóvenes-promesas"
        class="btn-bochas-rojo text-sm sm:text-base"
      >
        Volver a Jóvenes Bochófilos
      </router-link>
    </section>

    <!-- Detalle -->
    <article v-else class="pb-12 sm:pb-16">
      <!-- Banner con foto -->
      <section class="py-12 lg:py-24 px-4 sm:px-6 lg:px-8 bg-degradado-bochas">
        <div class="max-w-7xl mx-auto">
          <router-link
            to="/jóvenes-promesas"
            class="text-xs sm:text-sm text-white/80 hover:text-white mb-6 inline-block"
          >
            ← Volver a Jóvenes Bochófilos
          </router-link>
          <div class="flex flex-col sm:flex-row items-center gap-6">
            <!-- Foto circular grande -->
            <div
              class="w-32 h-32 sm:w-40 sm:h-40 lg:w-48 lg:h-48 rounded-full overflow-hidden bg-bochas-bg flex items-center justify-center flex-shrink-0 border-4 border-white/10"
            >
              <img
                v-if="joven.photo_url"
                :src="joven.photo_url"
                :alt="joven.name"
                class="w-full h-full object-cover"
              />
              <div
                v-else
                class="w-full h-full bg-bochas-bg flex items-center justify-center"
              >
                <span class="text-white text-5xl lg:text-6xl font-bold">{{
                  initials
                }}</span>
              </div>
            </div>
            <div>
              <h1
                class="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-2 text-center sm:text-left"
              >
                {{ joven.name }}
              </h1>
              <p
                v-if="joven.association"
                class="text-base sm:text-lg text-white/90 text-center sm:text-left"
              >
                {{ joven.association }}
              </p>
              <p
                v-if="joven.careerStartDate || joven.careerEndDate || joven.yearsPlaying > 0"
                class="text-sm text-white/70 text-center sm:text-left mt-1"
              >
                {{ careerPeriod(joven, `${joven.yearsPlaying} años jugando`) }}
              </p>
            </div>
          </div>
        </div>
      </section>

      <div
        v-if="joven.phone || joven.email || joven.whatsapp || joven.facebookUrl || joven.instagramUrl"
        class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8"
      >
        <PlayerContactLinks :player="joven" />
      </div>

      <section
        v-if="joven.achievements?.length"
        class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12"
      >
        <div class="card-fbochas p-6 sm:p-10">
          <div class="mb-4 flex items-center gap-3">
            <IconFbochas name="trophy" :size="26" color="#d32929" />
            <h2 class="text-xl sm:text-2xl font-bold text-white">Logros</h2>
          </div>
          <ul class="space-y-3">
            <li
              v-for="(achievement, index) in joven.achievements"
              :key="index"
              class="flex items-start gap-3 text-white"
            >
              <span class="mt-1 text-bochas-rojo">•</span>
              <span>{{ achievement }}</span>
            </li>
          </ul>
        </div>
      </section>

      <!-- Bio -->
      <section
        v-if="joven.bio"
        class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12"
      >
        <div class="card-fbochas p-6 sm:p-10">
          <h2 class="text-xl sm:text-2xl font-bold text-white mb-4">
            Sobre {{ joven.name.split(" ")[0] }}
          </h2>
          <RichTextContent
            :html="joven.bio"
            class="text-bochas-text-muted leading-relaxed text-base sm:text-lg"
          />
        </div>
      </section>
    </article>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import { obtenerJoven } from "@/services/youngPlayers/youngPlayersService";
import { careerPeriod } from "@/utils/playerCareer";
import IconFbochas from "@/components/Icons/IconFbochas.vue";
import PlayerContactLinks from "@/components/Common/PlayerContactLinks.vue";
import RichTextContent from "@/components/Common/RichTextContent.vue";
import { applyContentSeo, applyNotFoundSeo } from "@/services/seo/seoService";

const route = useRoute();
const joven = ref(null);
const loading = ref(true);

const initials = computed(() => {
  if (!joven.value?.name) return "";
  return joven.value.name
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase();
});

async function load() {
  loading.value = true;
  joven.value = null;
  try {
    joven.value = await obtenerJoven(route.params.id);
    await applyContentSeo({
      title: joven.value.name,
      description: joven.value.bio,
      image: joven.value.photo_url,
    });
  } catch {
    joven.value = null;
    applyNotFoundSeo();
  } finally {
    loading.value = false;
  }
}

onMounted(load);
watch(() => route.params.id, load);
</script>
